import { Toaster as Sonner } from "sonner"
import { useTheme } from "@/lib/contexts/ThemeContext"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Alert01Icon,
  CancelCircleIcon,
  CheckmarkCircle01Icon,
  InformationCircleIcon,
  Loading01Icon,
} from "@hugeicons/core-free-icons"

type ToasterProps = React.ComponentProps<typeof Sonner>

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme } = useTheme()

  return (
    <Sonner
      theme={theme}
      // Top-right avoids the bottom-right corner where the AI ask bar/editor
      // panel and page CTAs live — a toast there used to block clicks until
      // it dismissed itself.
      position="top-right"
      offset={16}
      gap={8}
      visibleToasts={3}
      icons={{
        // Success stays quiet: a small emerald check, no colored chrome.
        success: (
          <HugeiconsIcon icon={CheckmarkCircle01Icon} size={16} className="text-success" />
        ),
        error: (
          <HugeiconsIcon icon={CancelCircleIcon} size={16} className="text-destructive" />
        ),
        warning: (
          <HugeiconsIcon icon={Alert01Icon} size={16} className="text-warning" />
        ),
        info: (
          <HugeiconsIcon icon={InformationCircleIcon} size={16} className="text-muted-foreground" />
        ),
        loading: (
          <HugeiconsIcon icon={Loading01Icon} size={16} className="animate-spin text-foreground/60" />
        ),
      }}
      toastOptions={{
        // sonner's stylesheet is unlayered, so it outranks Tailwind's layered
        // utilities; every override here needs `!`.
        classNames: {
          // Raised white card (8% ink ring + one soft drop), same recipe as
          // dialogs and popovers. Errors and warnings keep the white card; a
          // tinted ring stops them getting lost among quiet successes.
          toast: [
            "items-start! gap-2.5! rounded-xl! border-0! bg-card! px-4! py-3! text-card-foreground! font-sans!",
            "shadow-[0_0_0_1px_rgb(9_9_11/0.08),0_12px_24px_-8px_rgb(0_0_0/0.14)]! dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08),0_12px_24px_-8px_rgb(0_0_0/0.5)]!",
            "data-[type=error]:shadow-[0_0_0_1px_#FECDCA,0_12px_24px_-8px_rgb(0_0_0/0.14)]! dark:data-[type=error]:shadow-[0_0_0_1px_rgb(240_68_56/0.35)]!",
            "data-[type=warning]:shadow-[0_0_0_1px_#FEDF89,0_12px_24px_-8px_rgb(0_0_0/0.14)]! dark:data-[type=warning]:shadow-[0_0_0_1px_rgb(247_144_9/0.35)]!",
          ].join(" "),
          icon: "mt-0.5! size-4!",
          title: "text-[13px]! font-medium! leading-5! text-foreground!",
          description: "text-xs! leading-[18px]! text-foreground/60!",
          actionButton: "h-7! rounded-md! bg-foreground! px-2.5! text-xs! font-medium! text-background!",
          cancelButton: "h-7! rounded-md! bg-muted! px-2.5! text-xs! text-foreground/70!",
          closeButton: "border-0! bg-card! text-foreground/60! shadow-[0_0_0_1px_rgb(9_9_11/0.08)]!",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
